import type { DetectionProfile } from '../types/cardReaderProfiles';

/** Bundled stock photos (copied from skimbuster-mobile `assets/card_reader_images`). Longer / more specific names first. */
const CARD_READER_STOCK_FILES: readonly string[] = [
  'Verifone_MX_915_Commander.webp',
  'Invenco_FlexPay.webp',
  'Ingenico_Move_5000.webp',
  'Ingenico_Lane_7000.webp',
  'Ingenico_Lane_5000.webp',
  'Ingenico_Lane_3000.webp',
  'Ingenico_Desk_5000.webp',
  'Verifone_MX_925.webp',
  'Verifone_MX_915.webp',
  'Verifone_UX300.webp',
  'Verifone_VX_805.webp',
  'Verifone_VX_520.webp',
  'Verifone_M400.webp',
  'Verifone_P400.webp',
  'PAX_S800.webp',
  'PAX_S300.webp',
  'PAX_A920.webp',
  'PAX_A80.webp',
];

function compactAlnum(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function stockFileCompactKey(filename: string): string {
  return compactAlnum(filename.replace(/\.webp$/i, ''));
}

/** Match profile name / reader model to a bundled stock image (same assets as mobile). */
function pickStockCardReaderImage(profile: DetectionProfile): string | undefined {
  const training = (profile as DetectionProfile & { trainingInfo?: { readerModel?: string } }).trainingInfo;
  const haystack = compactAlnum(
    [training?.readerModel, profile.name, profile.description].filter(Boolean).join(' ')
  );
  if (haystack.length < 3) return undefined;

  const sorted = [...CARD_READER_STOCK_FILES].sort(
    (a, b) => stockFileCompactKey(b).length - stockFileCompactKey(a).length
  );

  for (const file of sorted) {
    const key = stockFileCompactKey(file);
    if (haystack.includes(key)) {
      const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
      return `${base}/images/card_reader_images/${file}`;
    }
    if (key.includes(haystack) && haystack.length >= 6) {
      const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
      return `${base}/images/card_reader_images/${file}`;
    }
  }
  return undefined;
}

/** SVG placeholder when no Firebase photo — shows reader model / profile name (matches mobile “no photo yet” intent). */
function readerModelFallbackDataUrl(title: string): string {
  const safe = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  const lines =
    safe.length > 42 ? `${safe.slice(0, 40)}…` : safe;
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="560" height="360" viewBox="0 0 560 360">
  <defs>
    <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#0f172a"/>
      <stop offset="100%" style="stop-color:#1e293b"/>
    </linearGradient>
  </defs>
  <rect width="560" height="360" rx="16" fill="url(#bg)" stroke="#22d3ee" stroke-opacity="0.35" stroke-width="2"/>
  <text x="280" y="175" text-anchor="middle" fill="#94a3b8" font-family="system-ui,sans-serif" font-size="15">Reader</text>
  <text x="280" y="210" text-anchor="middle" fill="#e2e8f0" font-family="system-ui,sans-serif" font-size="18" font-weight="600">${lines}</text>
  <text x="280" y="250" text-anchor="middle" fill="#64748b" font-family="system-ui,sans-serif" font-size="12">No photo synced — add one on web or mobile</text>
</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** Same key fallbacks as Firestore mapping — some docs only store snake_case or legacy names. */
function pickHttpReaderImageUrl(profile: DetectionProfile): string | undefined {
  const p = profile as DetectionProfile & Record<string, unknown>;
  const keys = [
    'readerImageUrl',
    'reader_image_url',
    'readerImageURL',
    'ReaderImageUrl',
    'photoUrl',
    'readerPhotoUrl',
    'thumbnailUrl',
  ] as const;
  for (const k of keys) {
    const v = p[k];
    if (typeof v !== 'string') continue;
    const t = v.trim();
    if (t && /^https?:\/\//i.test(t)) return t;
  }
  const ti = p.trainingInfo;
  if (ti && typeof ti === 'object' && ti !== null) {
    const v = (ti as { readerImageUrl?: string }).readerImageUrl;
    if (typeof v === 'string') {
      const t = v.trim();
      if (t && /^https?:\/\//i.test(t)) return t;
    }
  }
  return undefined;
}

/**
 * Resolves the image `src` for a detection profile card.
 * - Uses `readerImageUrl` when it looks like a remote URL (same as mobile after upload).
 * - Else uses bundled stock photos in `/images/card_reader_images` when name/model matches (e.g. Verifone UX300).
 * - Otherwise builds a labeled SVG placeholder.
 */
export function getProfileReaderImageSrc(profile: DetectionProfile): string {
  const raw = pickHttpReaderImageUrl(profile);
  if (raw) {
    return raw;
  }
  const stock = pickStockCardReaderImage(profile);
  if (stock) {
    return stock;
  }
  const training = (profile as DetectionProfile & { trainingInfo?: { readerModel?: string } }).trainingInfo;
  const label =
    training?.readerModel?.trim() ||
    profile.description?.trim() ||
    profile.name?.trim() ||
    'Card reader';
  return readerModelFallbackDataUrl(label);
}
